export type ActivityEventSlug =
  | "user-created"
  | "user-updated"
  | "user-deleted"
  | "team-created"
  | "project-created"
  | "workflow-created"
  | "workflow-execution-started"
  | "workflow-execution-completed"
  | "workflow-execution-failed"
  | "workflow-execution-cancelled"
  | "asset-created"
  | "asset-confirmed"
  | "user-joined-team"
  | "workflow-metadata-updated"
  | "workflow-execution-requested"
  | "workflow-execution-pending-cancellation"
  | "asset-deleted"
  | "project-updated"
  | "workflow-version-deleted"
  | "workflow-version-restored"
  | "user-left-team"
  | "timeline-created"
  | "timeline-updated"
  | "timeline-version-created"
  | "timeline-version-deleted"
  | "workflow-node-added"
  | "workflow-node-removed"
  | "workflow-node-configured"
  | "workflow-connection-added"
  | "workflow-connection-removed"
  | "asset-undeleted"
  | "asset-purged"
  | "workflow-version-undeleted"
  | "workflow-version-purged"
  | "organisation-created"
  | "organisation-updated"
  | "organisation-suspended"
  | "organisation-restored"
  | "organisation-deleted"
  | "organisation-member-invited"
  | "organisation-member-joined"
  | "organisation-member-role-changed"
  | "organisation-member-suspended"
  | "organisation-member-removed"
  | "organisation-role-created"
  | "organisation-role-updated"
  | "organisation-role-deleted"
  | "organisation-role-permissions-updated"
  | "organisation-sso-config-updated"
  | "scim-user-provisioned"
  | "scim-user-deprovisioned"
  | "project-deleted"
  | "project-undeleted"
  | "project-purged"
  | "workflow-deleted"
  | "workflow-undeleted"
  | "workflow-purged"
  | "workflow-duplicated"
  | "asset-updated"
  | "organisation-sharing-domain-added"
  | "organisation-sharing-domain-removed"
  | "organisation-sharing-restriction-updated";

export type ActivityEventCategory =
  "all" | "workflows" | "assets" | "timelines" | "team" | "organisations";

export const ACTIVITY_CATEGORIES: {
  key: ActivityEventCategory;
  label: string;
  slugPrefixes: string[];
}[] = [
  { key: "all", label: "All", slugPrefixes: [] },
  {
    key: "workflows",
    label: "Workflows",
    slugPrefixes: ["workflow-"],
  },
  { key: "assets", label: "Assets", slugPrefixes: ["asset-"] },
  {
    key: "timelines",
    label: "Timelines",
    slugPrefixes: ["timeline-"],
  },
  {
    key: "team",
    label: "Team",
    slugPrefixes: ["user-", "team-"],
  },
  {
    key: "organisations",
    label: "Organisations",
    slugPrefixes: ["organisation-", "scim-"],
  },
];

export const EVENT_TYPES_BY_CATEGORY: Record<
  Exclude<ActivityEventCategory, "all">,
  ActivityEventSlug[]
> = {
  workflows: [
    "workflow-created",
    "workflow-metadata-updated",
    "workflow-execution-requested",
    "workflow-execution-started",
    "workflow-execution-completed",
    "workflow-execution-failed",
    "workflow-execution-cancelled",
    "workflow-execution-pending-cancellation",
    "workflow-version-deleted",
    "workflow-version-restored",
    "workflow-node-added",
    "workflow-node-removed",
    "workflow-node-configured",
    "workflow-connection-added",
    "workflow-connection-removed",
    "workflow-version-undeleted",
    "workflow-version-purged",
  ],
  assets: [
    "asset-created",
    "asset-confirmed",
    "asset-deleted",
    "asset-undeleted",
    "asset-purged",
    "asset-updated",
  ],
  timelines: [
    "timeline-created",
    "timeline-updated",
    "timeline-version-created",
    "timeline-version-deleted",
  ],
  team: [
    "user-created",
    "user-updated",
    "user-deleted",
    "team-created",
    "user-joined-team",
    "user-left-team",
  ],
  organisations: [
    "organisation-created",
    "organisation-updated",
    "organisation-suspended",
    "organisation-restored",
    "organisation-deleted",
    "organisation-member-invited",
    "organisation-member-joined",
    "organisation-member-role-changed",
    "organisation-member-suspended",
    "organisation-member-removed",
    "organisation-role-created",
    "organisation-role-updated",
    "organisation-role-deleted",
    "organisation-role-permissions-updated",
    "organisation-sso-config-updated",
    "scim-user-provisioned",
    "scim-user-deprovisioned",
    "organisation-sharing-domain-added",
    "organisation-sharing-domain-removed",
    "organisation-sharing-restriction-updated",
  ],
};

export interface StatusObject {
  id: string;
  slug: string;
  tk: string;
}

export interface ActivityEvent {
  id: string;
  createdAt: string;
  eventType: {
    id: string;
    slug: ActivityEventSlug;
    tk: string;
  };
  actor: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl: string | null;
  } | null;
  workflowId: string | null;
  workflowVersionId: string | null;
  workflowExecutionId: string | null;
  assetId: string | null;
  projectId: string | null;
  oldStatus: StatusObject | null;
  newStatus: StatusObject | null;
  eventDetails: Record<string, unknown> | null;
  eventDiffs: Record<string, { old: unknown; new: unknown }> | null;
}

export interface ActivityFeedPagination {
  pageSize: number;
  returnedCount: number;
  hasNext: boolean;
  nextCursor: string | null;
}

export interface ActivityFeedResponse {
  data: ActivityEvent[];
  meta: {
    pagination: ActivityFeedPagination;
  };
  correlationId?: string;
  eventId?: string;
}
